import React from 'react';

// Chart Component with theme support
export const DimensionChart = ({ data, filters, totalSessions, isDarkMode }) => {
  if (!data.length) {
    return (
      <div className="text-center py-12">
        <div className="text-6xl mb-4">📊</div>
        <h3 className={`text-xl font-semibold mb-2 ${
          isDarkMode ? 'text-white' : 'text-slate-800'
        }`}>Nenhum dado encontrado para gráficos</h3>
        <p className={`${
          isDarkMode ? 'text-gray-300' : 'text-slate-600'
        }`}>de acordo com os filtros selecionados. Tente ajustar os filtros para visualizar os gráficos.</p>
      </div>
    );
  }

  const saveChartAsImage = async () => {
    const html2canvas = (await import('html2canvas')).default;
    
    const chartElement = document.getElementById('dimension-charts');
    if (!chartElement) return;

    try {
      const saveButton = document.getElementById('save-chart-btn');
      if (saveButton) {
        saveButton.textContent = 'Gerando...';
        saveButton.disabled = true;
      }

      // Force light mode for chart export
      const wasInDarkMode = document.documentElement.classList.contains('dark');
      if (wasInDarkMode) {
        document.documentElement.classList.remove('dark');
      }

      // Wait a moment for the styles to apply
      await new Promise(resolve => setTimeout(resolve, 100));

      const canvas = await html2canvas(chartElement, {
        backgroundColor: '#ffffff',
        scale: 2,
        logging: false,
        useCORS: true,
        allowTaint: true
      });

      // Restore dark mode if it was active
      if (wasInDarkMode) {
        document.documentElement.classList.add('dark');
      }

      const link = document.createElement('a');
      const filterString = [
        filters.tag,
        ...(filters.departments || []),
        ...(filters.company_times || [])
      ].filter(Boolean).join('-') || 'todos';
      link.download = `grafico-fatores-psicossociais-${filterString}-${new Date().toISOString().split('T')[0]}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();

      if (saveButton) {
        saveButton.textContent = 'Salvar Gráfico como Imagem';
        saveButton.disabled = false;
      }

      alert('Gráfico salvo com sucesso!');
    } catch (error) {
      console.error('Erro ao salvar gráfico:', error);
      alert('Erro ao salvar gráfico. Tente novamente.');
      
      // Restore dark mode if there was an error
      const wasInDarkMode = document.documentElement.classList.contains('dark');
      if (wasInDarkMode) {
        document.documentElement.classList.remove('dark');
        setTimeout(() => document.documentElement.classList.add('dark'), 100);
      }
      
      const saveButton = document.getElementById('save-chart-btn');
      if (saveButton) {
        saveButton.textContent = 'Salvar Gráfico como Imagem';
        saveButton.disabled = false;
      }
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h3 className={`text-xl font-bold ${
            isDarkMode ? 'text-white' : 'text-slate-800'
          }`}>📈 Gráficos por Dimensão</h3>
          <p className={`text-sm mt-1 ${
            isDarkMode ? 'text-gray-300' : 'text-slate-600'
          }`}>
            {totalSessions} {totalSessions === 1 ? 'respondente analisado' : 'respondentes analisados'}
          </p>
        </div>
        <button
          id="save-chart-btn"
          onClick={saveChartAsImage}
          className="bg-demoraes-green hover:bg-demoraes-green/90 text-white font-semibold py-2 px-4 rounded-lg transition-all duration-200 hover:scale-105 active:scale-95 flex items-center"
        >
          💾 Salvar Gráfico como Imagem
        </button>
      </div>

      {/* External wrapper for dark mode theming */}
      <div className={`rounded-xl p-6 ${
        isDarkMode ? 'bg-gray-700' : 'bg-slate-50'
      }`}>
        {/* Inner div for export - always white background */}
        <div id="dimension-charts" className="space-y-6 bg-white p-6 rounded-xl">
        <div className="text-center mb-6 print-header">
          <img 
            src="/logo-new.png" 
            alt="DEMoraes Consultoria" 
            className="w-48 mx-auto mb-4"
          />
          <h2 className="text-2xl font-bold text-slate-800 mb-2">
            Mapeamento de Fatores Psicossociais
          </h2>
          <p className="text-slate-600">
            Distribuição das Respostas por Dimensão
          </p>
          <div className="text-sm text-slate-500 mt-2 space-y-1">
            {filters.tag && <div>Cliente: {filters.tag}</div>}
            {filters.departments && filters.departments.length > 0 && (
              <div>Setores: {filters.departments.join(', ')}</div>
            )}
            {filters.company_times && filters.company_times.length > 0 && (
              <div>Tempo de empresa: {filters.company_times.join(', ')}</div>
            )}
            <p>Total de respondentes: {totalSessions} • Data: {new Date().toLocaleDateString('pt-BR')}</p>
          </div>
        </div>

        {data.map((dimension) => {
          const total = dimension.positive + dimension.neutral + dimension.negative;
          const positivePercent = total > 0 ? (dimension.positive / total) * 100 : 0;
          const neutralPercent = total > 0 ? (dimension.neutral / total) * 100 : 0;
          const negativePercent = total > 0 ? (dimension.negative / total) * 100 : 0;

          return (
            <div key={dimension.dimension_key} className="bg-slate-50 p-6 rounded-xl border border-slate-200">
              <div className="flex justify-between items-center mb-4">
                <h4 className="text-lg font-bold text-slate-800">{dimension.dimension}</h4>
                <div className="text-right">
                  {dimension.prevalence ? (
                    <div className="text-sm text-slate-600">
                      Trabalhadores expostos: <span className="font-bold text-demoraes-green text-lg">{Number(dimension.average_score).toFixed(1)}%</span>
                    </div>
                  ) : (
                    <div className="text-sm text-slate-600">
                      Pontuação: <span className="font-bold text-demoraes-green text-lg">{Number(dimension.average_score).toFixed(1)}/100</span>
                    </div>
                  )}
                  <div className="text-xs text-slate-500">
                    {dimension.prevalence && dimension.respondents != null
                      ? `${dimension.exposed_count} de ${dimension.respondents} respondentes expostos`
                      : `${total} respostas`}
                  </div>
                </div>
              </div>
              
              <div className="w-full bg-slate-200 rounded-full h-10 overflow-hidden mb-4">
                <div className="h-full flex">
                  <div 
                    className="bg-red-500 h-full transition-all duration-1000 flex items-center justify-center"
                    style={{ width: `${negativePercent}%` }}
                  >
                    {negativePercent > 10 && (
                      <span className="text-white text-xs font-semibold">
                        {negativePercent.toFixed(0)}%
                      </span>
                    )}
                  </div>
                  <div 
                    className="bg-yellow-500 h-full transition-all duration-1000 flex items-center justify-center"
                    style={{ width: `${neutralPercent}%` }}
                  >
                    {neutralPercent > 10 && (
                      <span className="text-white text-xs font-semibold">
                        {neutralPercent.toFixed(0)}%
                      </span>
                    )}
                  </div>
                  <div 
                    className="bg-demoraes-green h-full transition-all duration-1000 flex items-center justify-center"
                    style={{ width: `${positivePercent}%` }}
                  >
                    {positivePercent > 10 && (
                      <span className="text-white text-xs font-semibold">
                        {positivePercent.toFixed(0)}%
                      </span>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div className="flex items-center justify-center bg-red-50 p-3 rounded-lg">
                  <div className="w-4 h-4 bg-red-500 rounded mr-2"></div>
                  <div className="text-center">
                    <div className="font-semibold text-red-700">Negativas</div>
                    <div className="text-slate-600">{dimension.negative} ({negativePercent.toFixed(1)}%)</div>
                  </div>
                </div>
                <div className="flex items-center justify-center bg-yellow-50 p-3 rounded-lg">
                  <div className="w-4 h-4 bg-yellow-500 rounded mr-2"></div>
                  <div className="text-center">
                    <div className="font-semibold text-yellow-700">Neutras</div>
                    <div className="text-slate-600">{dimension.neutral} ({neutralPercent.toFixed(1)}%)</div>
                  </div>
                </div>
                <div className="flex items-center justify-center bg-green-50 p-3 rounded-lg">
                  <div className="w-4 h-4 bg-demoraes-green rounded mr-2"></div>
                  <div className="text-center">
                    <div className="font-semibold" style={{color: 'var(--color-demoraes-green)'}}>Positivas</div>
                    <div className="text-slate-600">{dimension.positive} ({positivePercent.toFixed(1)}%)</div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}

        <div className="text-center mt-8 pt-6 border-t border-slate-200 print-footer">
          <p className="text-xs text-slate-400">
            Gráfico gerado pelo Sistema de Mapeamento de Fatores Psicossociais — escala COPSOQ 0–100
          </p>
          <p className="text-xs text-slate-400">
            <span className="font-semibold" style={{color: 'var(--color-demoraes-green)'}}>DEMoraes Consultoria</span> - 
            Juntos, transformamos segurança em cultura!
          </p>
        </div>
      </div>
      </div>
    </div>
  );
};
